import { agroApi } from "shared/api/agroApi";

import type { ApiEnvelope, PagedResponse } from "shared/model/api";
import type {
  HandbookList,
  HandbookListFilter,
} from "features/handbookList/model/handbookList";

type PageResp = PagedResponse<HandbookList>;
type ItemResp = ApiEnvelope<HandbookList>;

type CreateOrUpdateDto = {
  code: string;
  nameRu: string;
  nameKy: string;
  serviceCodes: string[];
};

export const handbooksApi = agroApi.injectEndpoints({
  endpoints: (builder) => ({
    // POST /dictionary/get-all
    getHandbooksList: builder.query<PageResp, HandbookListFilter>({
      query: (body) => ({
        url: "/dictionary/get-all",
        method: "POST",
        body: JSON.stringify(body),
      }),
      providesTags: (res) =>
        res?.res?.content
          ? [
              ...res.res.content.map((item) => ({
                type: "Handbooks" as const,
                id: item.code, // ТОЛЬКО string
              })),
              { type: "Handbooks", id: "LIST" },
            ]
          : [{ type: "Handbooks", id: "LIST" }],
    }),

    // GET /dictionary/{code}
    getHandbookByCode: builder.query<ItemResp, { code: string }>({
      query: ({ code }) => ({
        url: `/dictionary/${encodeURIComponent(code)}`,
        method: "GET",
      }),
      providesTags: (res, _err, arg) => [
        { type: "Handbooks", id: arg.code },
        { type: "Handbooks", id: "LIST" },
      ],
    }),

    // POST /dictionary  (создание)
    addHandbooksList: builder.mutation<ItemResp, { body: CreateOrUpdateDto }>({
      query: ({ body }) => ({
        url: "/dictionary",
        method: "POST",
        body: JSON.stringify(body),
      }),
      invalidatesTags: [{ type: "Handbooks", id: "LIST" }],
    }),

    // PUT /dictionary  (обновление)
    editHandbooksList: builder.mutation<ItemResp, { body: CreateOrUpdateDto }>({
      query: ({ body }) => ({
        url: "/dictionary",
        method: "PUT",
        body: JSON.stringify(body),
      }),
      invalidatesTags: (_res, _err, arg) => [
        { type: "Handbooks", id: arg.body.code },
        { type: "Handbooks", id: "LIST" },
      ],
    }),

    // DELETE /dictionary/{code}
    removeHandbooksList: builder.mutation<ItemResp, { code: string }>({
      query: ({ code }) => ({
        url: `/dictionary/${encodeURIComponent(code)}`,
        method: "DELETE",
      }),
      invalidatesTags: (_res, _err, arg) => [
        { type: "Handbooks", id: arg.code },
        { type: "Handbooks", id: "LIST" },
      ],
    }),

    // POST /admin/dictionaries/sync-codes
    syncDictionaryCodes: builder.mutation<ApiEnvelope<unknown>, void>({
      query: () => ({
        url: "/admin/dictionaries/sync-codes",
        method: "POST",
      }),
      invalidatesTags: [{ type: "Handbooks", id: "LIST" }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useLazyGetHandbooksListQuery,
  useGetHandbookByCodeQuery,
  useAddHandbooksListMutation,
  useEditHandbooksListMutation,
  useRemoveHandbooksListMutation,
  useSyncDictionaryCodesMutation,
} = handbooksApi;
